import React, { useState, useEffect, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { CollectionClientNote } from '../model';

interface CollectionClientNotesModalProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  company?: string;
  onNotesChanged?: () => void;
}

const formatTimestamp = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const CollectionClientNotesModal: React.FC<CollectionClientNotesModalProps> = ({
  open,
  onClose,
  clientId,
  company,
  onNotesChanged,
}) => {
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  const [notes, setNotes] = useState<CollectionClientNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'COLLECTION' | 'COLLECTIONRPL'>('COLLECTION');
  const [submitting, setSubmitting] = useState(false);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/collections/clients/${clientId}/notes`);
      setNotes(response.data || []);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch notes';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newNote.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`/collections/clients/${clientId}/notes`, {
        note: { body: newNote.trim(), servman_type: noteType },
      });
      setNewNote('');
      setNoteType('COLLECTION');
      fetchNotes();
      onNotesChanged?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to add note';
      showErrorSnackBar(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    try {
      await axios.delete(`/collections/clients/${clientId}/notes/${noteId}`);
      showSuccessSnackBar('Note deleted');
      fetchNotes();
      onNotesChanged?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to delete note';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    if (open && clientId) {
      fetchNotes();
      setNewNote('');
      setNoteType('COLLECTION');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Notes</Typography>
            {company && (
              <Typography variant="caption" color="text.secondary">
                {company}
              </Typography>
            )}
          </Box>
          <IconButton edge="end" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : notes.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No notes yet. Add one below.
            </Typography>
          </Box>
        ) : (
          <Box>
            {notes.map((note, index) => (
              <Box key={note.id}>
                <Box py={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" minWidth={0}>
                          {note.servman_type && (
                            <Typography variant="caption" color="text.secondary">
                              {note.servman_type}
                            </Typography>
                          )}
                          <Typography variant="subtitle2" color="primary">
                            {note.imported_from_servman
                              ? note.added_by || 'Servman'
                              : note.user_name || 'Unknown User'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                          {formatTimestamp(note.display_at)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                        {note.body}
                      </Typography>
                    </Box>
                    {/* Removed delete button for now, we don't want to delete Servman notes till Kevin confirms it */}
                    {/* {!note.imported_from_servman && (
                      <Tooltip title="Delete note">
                        <IconButton size="small" onClick={() => handleDelete(note.id)} sx={{ ml: 1 }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )} */}
                  </Box>
                </Box>
                {index < notes.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <TextField
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a note"
          fullWidth
          multiline
          maxRows={3}
          size="small"
          disabled={submitting}
        />
        <Select
          value={noteType}
          onChange={(e) => setNoteType(e.target.value as 'COLLECTION' | 'COLLECTIONRPL')}
          size="small"
          disabled={submitting}
          sx={{
            ml: 1,
            minWidth: 140,
            fontSize: '1rem',
            '& .MuiSelect-select': { fontSize: '0.75rem' },
          }}
        >
          <MenuItem value="COLLECTION" sx={{ fontSize: '0.75rem' }}>
            COLLECTION
          </MenuItem>
          <MenuItem value="COLLECTIONRPL" sx={{ fontSize: '0.75rem' }}>
            COLLECTIONRPL
          </MenuItem>
        </Select>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!newNote.trim() || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
          sx={{ ml: 1, minWidth: 90 }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectionClientNotesModal;
